// ============================================================================
//  États Financiers SYSCOHADA 2018
//  BalanceSheetPage.tsx — Bilan Actif/Passif
//  IncomeStatementPage.tsx — Compte de Résultat
//  CashFlowTftPage.tsx — Tableau des Flux de Trésorerie ⛔ (pas TAFIRE)
// ============================================================================

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  RefreshCw, Download, CheckCircle, AlertTriangle,
  TrendingUp, TrendingDown, Minus, ArrowRight, Lock,
} from 'lucide-react'
import { accountingApi } from '@/modules/accounting/api/accounting.api'
import { useNotificationStore } from '@/stores/notification.store'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat('fr-SN', { minimumFractionDigits: 0 }).format(Math.round(n))

const fmtSigned = (n: number) => (n >= 0 ? `+ ${fmt(n)}` : `- ${fmt(Math.abs(n))}`)

function FiscalYearSelect({ value, onChange, fiscalYears }: {
  value: string; onChange: (v: string) => void
  fiscalYears: Array<{ id: string; label: string; status: string }>
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
    >
      {fiscalYears.map(fy => (
        <option key={fy.id} value={fy.id}>{fy.label} ({fy.status})</option>
      ))}
    </select>
  )
}

// ─── Composant section état financier ────────────────────────────────────────

function ReportSection({ title, items, total, colorClass = 'text-[var(--brand-dark)]' }: {
  title: string
  items: Array<{ label: string; ref?: string; netAmount?: number; amount?: number; grossAmount?: number }>
  total: number
  colorClass?: string
}) {
  return (
    <div>
      <div className="px-4 py-2 bg-[var(--surface-2)]/40 border-b border-[var(--bd)]">
        <h3 className="text-xs font-bold text-[var(--tx2)] uppercase tracking-wide">{title}</h3>
      </div>
      <div className="divide-y divide-[var(--bd)]">
        {items.map((item, i) => {
          const val = item.netAmount ?? item.amount ?? 0
          return (
            <div key={i} className="flex items-center justify-between px-4 py-2 text-sm hover:bg-[var(--surface-2)]/20">
              <div className="flex items-center gap-2 min-w-0">
                {item.ref && (
                  <span className="text-xs font-mono text-[var(--tx3)] w-6 shrink-0">{item.ref}</span>
                )}
                <span className="text-[var(--tx2)] truncate">{item.label}</span>
              </div>
              <span className={`font-medium tabular-nums shrink-0 ml-4 ${val === 0 ? 'text-[var(--tx3)]' : colorClass}`}>
                {fmt(val)} XOF
              </span>
            </div>
          )
        })}
      </div>
      <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--surface-2)]/20 border-t border-[var(--bd)]">
        <span className="text-xs font-bold text-[var(--brand-dark)] uppercase">Total {title}</span>
        <span className={`text-sm font-bold tabular-nums ${colorClass}`}>{fmt(total)} XOF</span>
      </div>
    </div>
  )
}

// ============================================================================
//  BILAN SYSCOHADA
// ============================================================================


// ─── Génération HTML rapport (Blob URL — sans token) ─────────────────────
function openReportHtml(title, subtitle, html, fyLabel) {
  const co = 'var(--sb-bg)', ac = 'var(--brand-orange)'
  const fullHtml = '<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">' +
    '<title>' + title + ' — ' + fyLabel + '</title>' +
    '<style>' +
    '@import url("https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800&display=swap");' +
    '*{box-sizing:border-box;margin:0;padding:0}' +
    'body{font-family:"Raleway","Helvetica Neue",Arial,sans-serif;font-size:12px;line-height:1.5;color:#111;background:#fff}' +
    '.page{max-width:794px;margin:0 auto;padding:36px 44px}' +
    '.print-bar{display:flex;justify-content:flex-end;gap:10px;margin-bottom:24px;padding-bottom:18px;border-bottom:1px solid #E5E7EB}' +
    '.btn{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border:none;border-radius:8px;font-size:12.5px;font-weight:600;cursor:pointer}' +
    '.btn-dark{background:linear-gradient(135deg,' + co + ' 0%,#2D1854 100%);color:#fff}' +
    '.btn-light{background:#F3F4F6;color:#374151;border:1px solid #E5E7EB}' +
    '.header{margin-bottom:24px;padding-bottom:16px;border-bottom:2.5px solid ' + co + '}' +
    '.report-title{font-size:18px;font-weight:800;color:' + co + '}' +
    '.report-sub{font-size:12px;color:#555;margin-top:3px}' +
    'table{width:100%;border-collapse:collapse;font-size:11.5px}' +
    'tr{border-bottom:1px solid #F3F4F6}tr:nth-child(even){background:#FAFAFA}' +
    'td,th{padding:7px 12px}td:last-child,th:last-child{text-align:right}' +
    '.thead{background:' + co + ';color:#fff}' +
    '.total-row td{font-weight:700;background:#F0F9FF;border-top:1.5px solid ' + ac + ';color:' + co + '}' +
    '.grand{background:linear-gradient(135deg,' + co + ' 0%,#2D1854 100%) !important;color:#fff !important;font-size:14px;font-weight:800}' +
    '.section-head{background:' + co + ';color:#fff;padding:8px 12px;font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin-bottom:0}' +
    '.footer{margin-top:20px;padding-top:12px;border-top:1px solid #E5E7EB;font-size:9.5px;color:#9CA3AF}' +
    '@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.page{padding:20px 26px}.print-bar{display:none!important}@page{size:A4;margin:12mm 8mm}}' +
    '</style></head><body><div class="page">' +
    '<div class="print-bar"><button class="btn btn-light" onclick="window.close()">✕ Fermer</button>' +
    '<button class="btn btn-dark" onclick="window.print()">🖨 Imprimer / PDF</button></div>' +
    '<div class="header"><div class="report-title">' + title + '</div>' +
    '<div class="report-sub">' + subtitle + ' · ' + fyLabel + ' · Généré le ' + new Date().toLocaleDateString('fr-SN') + '</div></div>' +
    html +
    '<div class="footer">Document généré par NexusCorp ERP · Conformité SYSCOHADA 2018</div>' +
    '</div></body></html>'
  const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener')
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}

function fmtXOF(n) {
  return new Intl.NumberFormat('fr-SN', { minimumFractionDigits: 0 }).format(n ?? 0) + ' XOF'
}

function buildBalanceSheetHtml(data) {
  if (!data) return '<p style="color:#999">Aucune donnée disponible.</p>'
  const renderItems = (items) => (items ?? []).map(i =>
    '<tr><td style="color:#9CA3AF;width:50px">' + (i.ref ?? '') + '</td><td>' + (i.label ?? i.name ?? '') + '</td><td>' + fmtXOF(i.netAmount ?? 0) + '</td></tr>'
  ).join('')
  const actif  = data.actif  ?? []
  const passif = data.passif ?? []
  return '<div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">' +
    '<div><div class="section-head">ACTIF</div>' +
    '<table><thead class="thead"><tr><th>Réf</th><th>Libellé</th><th>Net</th></tr></thead><tbody>' +
    renderItems(actif) +
    '<tr class="grand"><td colspan="2">TOTAL ACTIF</td><td>' + fmtXOF(data.totalActif ?? 0) + '</td></tr>' +
    '</tbody></table></div>' +
    '<div><div class="section-head">PASSIF</div>' +
    '<table><thead class="thead"><tr><th>Réf</th><th>Libellé</th><th>Montant</th></tr></thead><tbody>' +
    renderItems(passif) +
    '<tr class="grand"><td colspan="2">TOTAL PASSIF</td><td>' + fmtXOF(data.totalPassif ?? 0) + '</td></tr>' +
    '</tbody></table></div></div>' +
    (data.isEquilibrated
      ? '<div style="margin-top:14px;padding:10px 14px;background:#ECFDF5;border:1px solid rgba(16,185,129,.25);border-radius:8px;color:#065F46;font-size:12px">✓ Bilan équilibré</div>'
      : '<div style="margin-top:14px;padding:10px 14px;background:#FEF2F2;border:1px solid rgba(220,38,38,.2);border-radius:8px;color:#991B1B;font-size:12px">⚠️ Bilan non équilibré</div>')
}

function buildIncomeStatementHtml(data) {
  if (!data) return '<p style="color:#999">Aucune donnée disponible.</p>'
  const renderLines = (lines) => (lines ?? []).map(l =>
    '<tr><td style="color:#9CA3AF;width:80px">' + l.code + '</td><td>' + l.name + '</td><td>' + fmtXOF(l.amount) + '</td></tr>'
  ).join('')
  const isPositive = (data.resultNet ?? 0) >= 0
  return '<div class="section-head" style="margin-bottom:8px">Produits (Classe 7)</div>' +
    '<table><thead class="thead"><tr><th>Compte</th><th>Libellé</th><th>Montant</th></tr></thead><tbody>' +
    renderLines(data.revenues?.lines ?? []) +
    '<tr class="total-row"><td colspan="2">TOTAL PRODUITS</td><td>' + fmtXOF(data.revenues?.total ?? 0) + '</td></tr>' +
    '</tbody></table>' +
    '<div class="section-head" style="margin:14px 0 8px">Charges (Classe 6)</div>' +
    '<table><thead class="thead"><tr><th>Compte</th><th>Libellé</th><th>Montant</th></tr></thead><tbody>' +
    renderLines(data.expenses?.lines ?? []) +
    '<tr class="total-row"><td colspan="2">TOTAL CHARGES</td><td>' + fmtXOF(data.expenses?.total ?? 0) + '</td></tr>' +
    '</tbody></table>' +
    '<div style="margin-top:14px;padding:14px 16px;background:' + (isPositive ? '#ECFDF5' : '#FEF2F2') + ';border-radius:10px;text-align:right">' +
    '<span style="font-size:13px;color:#555">Résultat net : </span>' +
    '<span style="font-size:18px;font-weight:800;color:' + (isPositive ? '#059669' : '#DC2626') + '">' + fmtXOF(data.resultNet ?? 0) + '</span></div>'
}

export function BalanceSheetPage() {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const [fyId, setFY] = useState('')

  const { data: fys = [] } = useQuery({ queryKey: ['fiscal-years'], queryFn: accountingApi.getFiscalYears })
  const activeFY = fyId || fys[0]?.id

  const { data: report, isLoading } = useQuery({
    queryKey: ['report-balance-sheet', activeFY],
    queryFn:  async () => {
      await accountingApi.generateBalanceSheet(activeFY!)
      // Re-fetch pour avoir les données générées — en pratique on récupère directement le retour
      return accountingApi.generateBalanceSheet(activeFY!)
    },
    enabled:  !!activeFY,
    staleTime: 1000 * 60 * 5,
  })

  const genMutation = useMutation({
    mutationFn: () => accountingApi.generateBalanceSheet(activeFY!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['report-balance-sheet', activeFY] })
      addNotification({ type: 'success', title: 'Bilan généré', message: 'Bilan SYSCOHADA 2018 mis à jour.' })
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message })
    },
  })

  const data = (report as any)?.data

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Bilan</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">SYSCOHADA 2018 · Actif / Passif</p>
        </div>
        <div className="flex items-center gap-2">
          <FiscalYearSelect value={fyId || fys[0]?.id || ''} onChange={setFY} fiscalYears={fys} />
          <button
            onClick={() => genMutation.mutate()}
            disabled={genMutation.isPending || !activeFY}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${genMutation.isPending ? 'animate-spin' : ''}`} />
            Générer
          </button>
          {data && (
            <button
              onClick={() => openReportHtml('Bilan', 'SYSCOHADA 2018 · Actif / Passif', buildBalanceSheetHtml(data), (fys as any[]).find((f: any) => f.id === activeFY)?.label ?? '')}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-[var(--bd)] hover:bg-[var(--surface-2)] text-[var(--tx2)] text-sm rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Imprimer / PDF
            </button>
          )}
        </div>
      </div>

      {/* Statut équilibre */}
      {data && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm ${
          data.isEquilibrated
            ? 'bg-[#10B981]/10 border-[rgba(16,185,129,0.25)] text-[#059669]'
            : 'bg-[#FEF2F2] border-[rgba(239,68,68,0.25)] text-[#DC2626]'
        }`}>
          {data.isEquilibrated
            ? <CheckCircle className="w-4 h-4" />
            : <AlertTriangle className="w-4 h-4" />
          }
          {data.isEquilibrated
            ? `Bilan équilibré · Total Actif = Total Passif = ${fmt(data.totalActif)} XOF`
            : `⚠️ Bilan NON équilibré · Actif ${fmt(data.totalActif)} ≠ Passif ${fmt(data.totalPassif)} XOF`
          }
        </div>
      )}

      {isLoading || !data ? (
        <div className="grid grid-cols-2 gap-6">
          {[0,1].map(i => <div key={i} className="h-40 bg-[var(--surface-2)] rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ACTIF */}
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--bd)] bg-[var(--blue)]/5">
              <h2 className="text-sm font-bold text-[var(--blue-d)]">ACTIF</h2>
              <p className="text-xs text-[var(--tx3)]">Emplois des ressources</p>
            </div>
            <ReportSection title="Actif immobilisé" items={data.actif?.filter((i: any) => ['AB','AC'].includes(i.ref)) ?? []} total={data.actif?.filter((i: any) => ['AB','AC'].includes(i.ref)).reduce((s: number, i: any) => s + i.netAmount, 0) ?? 0} colorClass="text-[var(--blue-d)]" />
            <ReportSection title="Actif circulant" items={data.actif?.filter((i: any) => ['BA','BJ'].includes(i.ref)) ?? []} total={data.actif?.filter((i: any) => ['BA','BJ'].includes(i.ref)).reduce((s: number, i: any) => s + i.netAmount, 0) ?? 0} colorClass="text-[var(--blue-d)]" />
            <ReportSection title="Trésorerie active" items={data.actif?.filter((i: any) => i.ref === 'BT') ?? []} total={data.actif?.filter((i: any) => i.ref === 'BT').reduce((s: number, i: any) => s + i.netAmount, 0) ?? 0} colorClass="text-[var(--blue-d)]" />
            <div className="flex items-center justify-between px-4 py-3 bg-[#E0F2FE] border-t border-[rgba(245,124,0,0.25)]">
              <span className="text-sm font-bold text-[var(--brand-dark)]">TOTAL ACTIF</span>
              <span className="text-base font-bold text-[var(--blue-d)]">{fmt(data.totalActif)} XOF</span>
            </div>
          </div>

          {/* PASSIF */}
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--bd)] bg-[#10B981]/5">
              <h2 className="text-sm font-bold text-[#059669]">PASSIF</h2>
              <p className="text-xs text-[var(--tx3)]">Ressources de financement</p>
            </div>
            <ReportSection title="Capitaux propres" items={data.passif?.filter((i: any) => ['CA','CB','CF'].includes(i.ref)) ?? []} total={data.passif?.filter((i: any) => ['CA','CB','CF'].includes(i.ref)).reduce((s: number, i: any) => s + i.netAmount, 0) ?? 0} colorClass="text-[#059669]" />
            <ReportSection title="Dettes financières" items={data.passif?.filter((i: any) => i.ref === 'DA') ?? []} total={data.passif?.filter((i: any) => i.ref === 'DA').reduce((s: number, i: any) => s + i.netAmount, 0) ?? 0} colorClass="text-[#059669]" />
            <ReportSection title="Dettes d'exploitation" items={data.passif?.filter((i: any) => ['DH','DJ'].includes(i.ref)) ?? []} total={data.passif?.filter((i: any) => ['DH','DJ'].includes(i.ref)).reduce((s: number, i: any) => s + i.netAmount, 0) ?? 0} colorClass="text-[#059669]" />
            <div className="flex items-center justify-between px-4 py-3 bg-[#10B981]/10 border-t border-[rgba(16,185,129,0.25)]">
              <span className="text-sm font-bold text-[var(--brand-dark)]">TOTAL PASSIF</span>
              <span className="text-base font-bold text-[#059669]">{fmt(data.totalPassif)} XOF</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  COMPTE DE RÉSULTAT
// ============================================================================

export function IncomeStatementPage() {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const [fyId, setFY] = useState('')

  const { data: fys = [] } = useQuery({ queryKey: ['fiscal-years'], queryFn: accountingApi.getFiscalYears })
  const activeFY = fyId || fys[0]?.id

  const genMutation = useMutation({
    mutationFn: () => accountingApi.generateIncomeStatement(activeFY!),
    onSuccess: (result) => {
      qc.setQueryData(['report-income', activeFY], result)
      addNotification({ type: 'success', title: 'Compte de résultat généré', message: '' })
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message })
    },
  })

  const data = (qc.getQueryData(['report-income', activeFY]) as any)?.data
    ?? (genMutation.data as any)?.data

  const resultNet = data?.resultNet ?? 0

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Compte de Résultat</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">SYSCOHADA 2018 · Charges / Produits</p>
        </div>
        <div className="flex items-center gap-2">
          <FiscalYearSelect value={fyId || fys[0]?.id || ''} onChange={setFY} fiscalYears={fys} />
          <button
            onClick={() => genMutation.mutate()}
            disabled={genMutation.isPending || !activeFY}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${genMutation.isPending ? 'animate-spin' : ''}`} />
            Générer
          </button>
          {(report as any)?.data && (
            <button
              onClick={() => openReportHtml('Compte de Résultat', 'SYSCOHADA 2018', buildIncomeStatementHtml((report as any)?.data), (fys as any[]).find((f: any) => f.id === activeFY)?.label ?? '')}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-[var(--bd)] hover:bg-[var(--surface-2)] text-[var(--tx2)] text-sm rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Imprimer / PDF
            </button>
          )}
        </div>
      </div>

      {genMutation.isPending && (
        <div className="text-center py-10 text-[var(--tx3)]">Génération en cours…</div>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Produits */}
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--bd)] bg-[#10B981]/5">
              <h2 className="text-sm font-bold text-[#059669] flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                PRODUITS — Classe 7
              </h2>
            </div>
            <div className="divide-y divide-[var(--bd)]">
              {(data.revenues?.lines ?? []).map((l: any, i: number) => (
                <div key={i} className="flex justify-between px-4 py-2 text-sm hover:bg-[var(--surface-2)]/20">
                  <span className="text-[var(--tx2)] flex items-center gap-2">
                    <code className="text-xs font-mono text-[var(--tx3)]">{l.code}</code>
                    {l.name}
                  </span>
                  <span className="text-[#059669] font-medium tabular-nums">{fmt(l.amount)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between px-4 py-3 bg-[#10B981]/10 border-t border-[rgba(16,185,129,0.25)]">
              <span className="text-sm font-bold text-[var(--brand-dark)]">Total Produits</span>
              <span className="text-sm font-bold text-[#059669]">{fmt(data.revenues?.total ?? 0)} XOF</span>
            </div>
          </div>

          {/* Charges */}
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--bd)] bg-red-500/5">
              <h2 className="text-sm font-bold text-[#DC2626] flex items-center gap-2">
                <TrendingDown className="w-4 h-4" />
                CHARGES — Classe 6
              </h2>
            </div>
            <div className="divide-y divide-[var(--bd)]">
              {(data.expenses?.lines ?? []).map((l: any, i: number) => (
                <div key={i} className="flex justify-between px-4 py-2 text-sm hover:bg-[var(--surface-2)]/20">
                  <span className="text-[var(--tx2)] flex items-center gap-2">
                    <code className="text-xs font-mono text-[var(--tx3)]">{l.code}</code>
                    {l.name}
                  </span>
                  <span className="text-[#DC2626] font-medium tabular-nums">{fmt(l.amount)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between px-4 py-3 bg-[#FEF2F2] border-t border-[rgba(239,68,68,0.25)]">
              <span className="text-sm font-bold text-[var(--brand-dark)]">Total Charges</span>
              <span className="text-sm font-bold text-[#DC2626]">{fmt(data.expenses?.total ?? 0)} XOF</span>
            </div>
          </div>
        </div>
      )}

      {/* Résultat net */}
      {data && (
        <div className={`flex items-center justify-between px-6 py-4 rounded-xl border-2 ${
          resultNet >= 0
            ? 'bg-[#10B981]/10 border-emerald-500/30'
            : 'bg-[#FEF2F2] border-[rgba(239,68,68,0.35)]'
        }`}>
          <div>
            <p className="text-sm text-[var(--tx3)]">Résultat net de l'exercice</p>
            <p className="text-xs text-[var(--tx3)] mt-0.5">
              {resultNet >= 0 ? '✅ Bénéfice' : '⚠️ Perte'} · {fys.find(f => f.id === activeFY)?.label}
            </p>
          </div>
          <span className={`text-2xl font-bold tabular-nums ${resultNet >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
            {fmtSigned(resultNet)} XOF
          </span>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  TFT — TABLEAU DES FLUX DE TRÉSORERIE SYSCOHADA 2018
//  ⛔ PAS TAFIRE — remplacé par TFT depuis SYSCOHADA 2018
// ============================================================================

export function CashFlowTftPage() {
  const qc = useQueryClient()
  const { addNotification } = useNotificationStore()
  const [fyId, setFY] = useState('')

  const { data: fys = [] } = useQuery({ queryKey: ['fiscal-years'], queryFn: accountingApi.getFiscalYears })
  const activeFY = fyId || fys[0]?.id

  const genMutation = useMutation({
    mutationFn: () => accountingApi.generateCashFlowTft(activeFY!),
    onSuccess: (result) => {
      qc.setQueryData(['report-tft', activeFY], result)
      addNotification({ type: 'success', title: 'TFT généré', message: 'Tableau des Flux de Trésorerie SYSCOHADA 2018 mis à jour.' })
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Erreur', message: e?.response?.data?.message })
    },
  })

  const data = (qc.getQueryData(['report-tft', activeFY]) as any)?.data
    ?? (genMutation.data as any)?.data

  // Sections TFT
  const sections = data ? [
    { key: 'operational', label: '1. Activités opérationnelles', color: 'text-[var(--blue-d)]',    bg: 'bg-[var(--blue)]/5',    border: 'border-[rgba(245,124,0,0.25)]' },
    { key: 'investment',  label: '2. Activités d\'investissement', color: 'text-[#D97706]',  bg: 'bg-[#FFFBEB]',   border: 'border-[rgba(245,158,11,0.25)]' },
    { key: 'financing',   label: '3. Activités de financement',   color: 'text-[#7C3AED]', bg: 'bg-[#F5F3FF]',  border: 'border-[rgba(124,58,237,0.25)]' },
  ] : []

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">
            Tableau des Flux de Trésorerie
          </h1>
          {/* ⛔ Mention explicite anti-TAFIRE */}
          <p className="text-sm text-[var(--tx3)] mt-0.5">
            TFT — SYSCOHADA 2018 · <span className="text-[#DC2626] line-through opacity-50">TAFIRE</span>
            <span className="text-[var(--tx3)] ml-1">(format pré-2018, interdit)</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FiscalYearSelect value={fyId || fys[0]?.id || ''} onChange={setFY} fiscalYears={fys} />
          <button
            onClick={() => genMutation.mutate()}
            disabled={genMutation.isPending || !activeFY}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${genMutation.isPending ? 'animate-spin' : ''}`} />
            Générer TFT
          </button>
        </div>
      </div>

      {/* Contrôle TFT */}
      {data && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm ${
          data.tftControlPassed
            ? 'bg-[#10B981]/10 border-[rgba(16,185,129,0.25)] text-[#059669]'
            : 'bg-[#FEF3C7] border-[rgba(245,158,11,0.25)] text-[#D97706]'
        }`}>
          {data.tftControlPassed
            ? <CheckCircle className="w-4 h-4 shrink-0" />
            : <AlertTriangle className="w-4 h-4 shrink-0" />
          }
          <span>
            <strong>Contrôle TFT :</strong>{' '}
            {data.tftControlPassed
              ? `Flux(1) + Flux(2) + Flux(3) = ΔTrésorerie = ${fmt(data.netVariation)} XOF ✓`
              : `⚠️ Flux calculés (${fmt(data.computedVariation)}) ≠ ΔTrésorerie (${fmt(data.netVariation)}) — Vérifiez les transactions`
            }
          </span>
        </div>
      )}

      {genMutation.isPending && (
        <div className="text-center py-10 text-[var(--tx3)]">Génération en cours…</div>
      )}

      {data && (
        <div className="space-y-4">
          {/* Soldes trésorerie */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Solde ouverture',  value: data.openingBalance, color: 'text-[var(--tx3)]' },
              { label: 'Variation nette',  value: data.netVariation,   color: data.netVariation >= 0 ? 'text-[#059669]' : 'text-[#DC2626]' },
              { label: 'Solde clôture',    value: data.closingBalance, color: 'text-[var(--blue-d)]' },
            ].map(item => (
              <div key={item.label} className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4 text-center">
                <p className="text-xs text-[var(--tx3)] mb-1">{item.label}</p>
                <p className={`text-lg font-bold tabular-nums ${item.color}`}>{fmt(item.value)} <span className="text-xs font-normal">XOF</span></p>
              </div>
            ))}
          </div>

          {/* 3 sections TFT */}
          {sections.map(({ key, label, color, bg, border }) => {
            const section = data[key]
            return (
              <div key={key} className={`rounded-xl border overflow-hidden ${border}`}>
                <div className={`px-4 py-3 border-b ${border} ${bg}`}>
                  <h3 className={`text-sm font-bold ${color}`}>{label}</h3>
                </div>
                <div className="bg-[var(--surface-2)] divide-y divide-[var(--bd)]">
                  {(section?.items ?? []).map((item: any, i: number) => (
                    <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="text-[var(--tx2)]">{item.label}</span>
                      <span className={`tabular-nums font-medium ${item.amount >= 0 ? color : 'text-[#DC2626]'}`}>
                        {fmtSigned(item.amount)} XOF
                      </span>
                    </div>
                  ))}
                  <div className={`flex items-center justify-between px-4 py-3 ${bg}`}>
                    <span className="text-xs font-bold text-[var(--brand-dark)] uppercase">Flux nets {label.split(' ').slice(2).join(' ')}</span>
                    <span className={`font-bold tabular-nums ${color}`}>{fmtSigned(section?.total ?? 0)} XOF</span>
                  </div>
                </div>
              </div>
            )
          })}

          {/* Vérification finale */}
          <div className="flex items-center justify-between px-6 py-4 bg-[var(--surface-2)]/30 rounded-xl border border-[var(--bd)]">
            <div className="text-sm text-[var(--tx3)]">
              <span className="text-[var(--blue-d)] font-medium">{fmt(data.operational?.total ?? 0)}</span>
              <span className="mx-2 text-[var(--tx3)]">+</span>
              <span className="text-[#D97706] font-medium">{fmt(data.investment?.total ?? 0)}</span>
              <span className="mx-2 text-[var(--tx3)]">+</span>
              <span className="text-[#7C3AED] font-medium">{fmt(data.financing?.total ?? 0)}</span>
              <span className="mx-2 text-[var(--tx3)]">=</span>
              <span className={data.tftControlPassed ? 'text-[#059669] font-bold' : 'text-[#DC2626] font-bold'}>
                {fmt(data.computedVariation ?? 0)} XOF
              </span>
            </div>
            <div className={`flex items-center gap-1.5 text-xs ${data.tftControlPassed ? 'text-[#059669]' : 'text-[#D97706]'}`}>
              {data.tftControlPassed ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              Contrôle TFT {data.tftControlPassed ? 'OK' : 'en attente'}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Export par défaut = BalanceSheetPage (l'entrée principale)
export default BalanceSheetPage
